import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2Icon,
  LockKeyholeIcon,
  SaveIcon,
  SettingsIcon,
  UserPlusIcon,
  XCircleIcon,
} from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import {
  WorkspaceToolUpdateSchema,
  type AppSummary,
  type WorkspaceAdminResponse,
} from "../../shared/schemas";
import { ApiClientError, updateWorkspaceTool, updateWorkspaceToolAccess } from "../api/client";
import { appsQuery, workspaceQuery } from "../api/queries";
import { cn } from "../lib/utils";
import { Alert, AlertDescription } from "./ui/alert";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Button, buttonVariants } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Field, FieldDescription, FieldError, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const iconOptions = [
  ["tool", "General tool"],
  ["quotes", "Quotes and records"],
  ["parts", "Parts and inventory"],
  ["table", "Tables and lists"],
  ["files", "Files"],
  ["listener", "Connections"],
] as const;

const roleLabels = {
  platform_admin: "Consultant",
  organization_admin: "Workspace administrator",
  member: "Member",
} as const;

type DetailErrors = {
  displayName?: string;
  description?: string;
  icon?: string;
};

function withoutDetailError(errors: DetailErrors, field: keyof DetailErrors): DetailErrors {
  return Object.fromEntries(Object.entries(errors).filter(([key]) => key !== field));
}

function userInitials(displayName: string) {
  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function parseDetailErrors(issues: { path: PropertyKey[]; message: string }[]): DetailErrors {
  const errors: DetailErrors = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (
      (field === "displayName" || field === "description" || field === "icon") &&
      !errors[field]
    ) {
      errors[field] = issue.message;
    }
  }
  return errors;
}

function ToolSettingsTabs({
  tab,
  onChange,
}: {
  tab: "details" | "access";
  onChange: (tab: "details" | "access") => void;
}) {
  return (
    <div aria-label="Tool settings sections" className="flex gap-6 border-b" role="tablist">
      {(["details", "access"] as const).map((item) => (
        <button
          aria-controls={`tool-settings-${item}`}
          aria-selected={tab === item}
          className={cn(
            "relative min-h-11 border-0 bg-transparent px-0 font-sans text-sm font-semibold text-muted-foreground outline-none transition-[color] duration-150 focus-visible:shadow-[var(--focus-ring)]",
            tab === item &&
              "text-link after:absolute after:right-0 after:bottom-[-1px] after:left-0 after:h-[2.5px] after:bg-link",
          )}
          id={`tool-settings-tab-${item}`}
          key={item}
          role="tab"
          type="button"
          onClick={() => {
            onChange(item);
          }}
        >
          {item === "details" ? "Details" : "User access"}
        </button>
      ))}
    </div>
  );
}

function ToolDetailsForm({
  controller,
  isOnline,
  tool,
}: {
  controller: ReturnType<typeof useToolSettingsController>;
  isOnline: boolean;
  tool: AppSummary;
}) {
  const clearError = (field: keyof DetailErrors) => {
    controller.setErrors((current) => withoutDetailError(current, field));
  };
  return (
    <form
      aria-labelledby="tool-settings-tab-details"
      className="grid gap-5"
      id="tool-settings-details"
      role="tabpanel"
      onSubmit={controller.submitDetails}
    >
      <Field data-invalid={Boolean(controller.errors.displayName)}>
        <FieldLabel htmlFor={`tool-name-${tool.appInstanceId}`}>Tool name</FieldLabel>
        <Input
          aria-invalid={Boolean(controller.errors.displayName)}
          disabled={controller.detailMutation.isPending}
          id={`tool-name-${tool.appInstanceId}`}
          maxLength={120}
          value={controller.displayName}
          onChange={(event) => {
            controller.setDisplayName(event.target.value);
            clearError("displayName");
          }}
        />
        <FieldError>{controller.errors.displayName}</FieldError>
      </Field>
      <Field data-invalid={Boolean(controller.errors.description)}>
        <FieldLabel htmlFor={`tool-description-${tool.appInstanceId}`}>Description</FieldLabel>
        <Textarea
          aria-invalid={Boolean(controller.errors.description)}
          disabled={controller.detailMutation.isPending}
          id={`tool-description-${tool.appInstanceId}`}
          maxLength={320}
          value={controller.description}
          onChange={(event) => {
            controller.setDescription(event.target.value);
            clearError("description");
          }}
        />
        <FieldDescription>Explain what the tool helps the team do.</FieldDescription>
        <FieldError>{controller.errors.description}</FieldError>
      </Field>
      <Field data-invalid={Boolean(controller.errors.icon)}>
        <FieldLabel htmlFor={`tool-icon-${tool.appInstanceId}`}>Icon</FieldLabel>
        <select
          aria-invalid={Boolean(controller.errors.icon)}
          className="min-h-10 w-full rounded-sm border border-input bg-card px-3 py-[9px] text-sm outline-none transition-[border-color,box-shadow] duration-150 focus-visible:border-primary focus-visible:shadow-[var(--field-focus-shadow)]"
          disabled={controller.detailMutation.isPending}
          id={`tool-icon-${tool.appInstanceId}`}
          value={controller.icon}
          onChange={(event) => {
            controller.setIcon(WorkspaceToolUpdateSchema.shape.icon.parse(event.target.value));
          }}
        >
          {iconOptions.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <FieldError>{controller.errors.icon}</FieldError>
      </Field>
      <DialogFooter>
        <DialogClose render={<Button type="button" variant="ghost" />}>Cancel</DialogClose>
        <Button disabled={controller.detailMutation.isPending || !isOnline} type="submit">
          <SaveIcon aria-hidden="true" data-icon="inline-start" /> Save changes
        </Button>
      </DialogFooter>
    </form>
  );
}

function ToolAccessRow({
  controller,
  isOnline,
  member,
  tool,
}: {
  controller: ReturnType<typeof useToolSettingsController>;
  isOnline: boolean;
  member: WorkspaceAdminResponse["members"][number];
  tool: AppSummary;
}) {
  const hasAccess = member.toolAccess.includes(tool.appInstanceId);
  const administrator = member.role !== "member";
  return (
    <div className="grid gap-3 border-b p-3 last:border-b-0 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar>
          <AvatarFallback>{userInitials(member.user.displayName)}</AvatarFallback>
        </Avatar>
        <span className="min-w-0 flex-1">
          <strong className="block truncate text-sm">{member.user.displayName}</strong>
          <span className="block truncate font-mono text-xs text-muted-foreground">
            @{member.user.username}
          </span>
        </span>
        <Badge variant="outline">{roleLabels[member.role]}</Badge>
      </div>
      <div className="flex min-w-36 items-center justify-end gap-2">
        {administrator ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <LockKeyholeIcon aria-hidden="true" className="size-3.5" /> Always allowed
          </span>
        ) : (
          <Button
            disabled={!isOnline || controller.accessMutation.isPending}
            size="sm"
            type="button"
            variant={hasAccess ? "outline" : "default"}
            onClick={() => {
              controller.accessMutation.mutate({
                membershipId: member.membershipId,
                granted: !hasAccess,
              });
            }}
          >
            {hasAccess ? (
              <XCircleIcon aria-hidden="true" data-icon="inline-start" />
            ) : (
              <CheckCircle2Icon aria-hidden="true" data-icon="inline-start" />
            )}
            {hasAccess ? "Remove access" : "Grant access"}
          </Button>
        )}
      </div>
    </div>
  );
}

function ToolAccessPanel({
  controller,
  isOnline,
  organizationId,
  tool,
  workspace,
}: {
  controller: ReturnType<typeof useToolSettingsController>;
  isOnline: boolean;
  organizationId: string;
  tool: AppSummary;
  workspace: WorkspaceAdminResponse;
}) {
  const accessCount = workspace.members.filter((member) =>
    member.toolAccess.includes(tool.appInstanceId),
  ).length;
  return (
    <div
      aria-labelledby="tool-settings-tab-access"
      className="grid gap-4"
      id="tool-settings-access"
      role="tabpanel"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <strong className="block text-sm">{accessCount} people can open this tool</strong>
          <span className="mt-1 block text-sm text-muted-foreground">
            Grant or remove access one person at a time.
          </span>
        </div>
        <a
          className={buttonVariants({ size: "sm", variant: "outline" })}
          href={`/workspace?org=${encodeURIComponent(organizationId)}&tab=users`}
        >
          <UserPlusIcon aria-hidden="true" data-icon="inline-start" /> Add user
        </a>
      </div>
      <div className="grid max-h-[52vh] overflow-y-auto rounded-lg border bg-background">
        {workspace.members.map((member) => (
          <ToolAccessRow
            controller={controller}
            isOnline={isOnline}
            key={member.membershipId}
            member={member}
            tool={tool}
          />
        ))}
      </div>
      <DialogFooter>
        <DialogClose render={<Button type="button" />}>Done</DialogClose>
      </DialogFooter>
    </div>
  );
}

function useToolSettingsController(organizationId: string, tool: AppSummary, onClosed: () => void) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(tool.displayName);
  const [description, setDescription] = useState(tool.description);
  const [icon, setIcon] = useState(tool.icon);
  const [errors, setErrors] = useState<DetailErrors>({});
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: appsQuery(organizationId).queryKey }),
      queryClient.invalidateQueries({ queryKey: workspaceQuery(organizationId).queryKey }),
    ]);
  };
  const detailMutation = useMutation({
    mutationFn: () =>
      updateWorkspaceTool(organizationId, tool.appInstanceId, { displayName, description, icon }),
    onSuccess: async () => {
      await refresh();
      onClosed();
    },
  });
  const accessMutation = useMutation({
    mutationFn: ({ membershipId, granted }: { membershipId: string; granted: boolean }) =>
      updateWorkspaceToolAccess(organizationId, tool.appInstanceId, membershipId, { granted }),
    onSuccess: refresh,
  });
  function submitDetails(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = WorkspaceToolUpdateSchema.safeParse({ displayName, description, icon });
    if (!parsed.success) {
      setErrors(parseDetailErrors(parsed.error.issues));
      return;
    }
    setErrors({});
    detailMutation.mutate();
  }
  function reset() {
    setDisplayName(tool.displayName);
    setDescription(tool.description);
    setIcon(tool.icon);
    setErrors({});
  }
  return {
    accessMutation,
    description,
    detailMutation,
    displayName,
    errors,
    icon,
    reset,
    setDescription,
    setDisplayName,
    setErrors,
    setIcon,
    submitDetails,
  };
}

export function ToolSettingsDialog({
  isOnline,
  organizationId,
  tool,
  workspace,
}: {
  isOnline: boolean;
  organizationId: string;
  tool: AppSummary;
  workspace: WorkspaceAdminResponse;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"details" | "access">("details");
  const controller = useToolSettingsController(organizationId, tool, () => {
    setOpen(false);
  });

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      controller.reset();
      setTab("details");
    }
    setOpen(nextOpen);
  }

  const mutationError = controller.detailMutation.error ?? controller.accessMutation.error;
  const errorMessage =
    mutationError instanceof ApiClientError
      ? mutationError.message
      : mutationError
        ? "The tool could not be updated. Try again."
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        render={
          <Button
            aria-label={`Settings for ${tool.displayName}`}
            size="icon"
            title={`Settings for ${tool.displayName}`}
            type="button"
            variant="outline"
          />
        }
      >
        <SettingsIcon aria-hidden="true" />
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Tool settings</DialogTitle>
          <DialogDescription>
            Change how {tool.displayName} appears and who can open it.
          </DialogDescription>
        </DialogHeader>

        <ToolSettingsTabs tab={tab} onChange={setTab} />

        {errorMessage ? (
          <Alert role="alert" variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}

        {tab === "details" ? (
          <ToolDetailsForm controller={controller} isOnline={isOnline} tool={tool} />
        ) : (
          <ToolAccessPanel
            controller={controller}
            isOnline={isOnline}
            organizationId={organizationId}
            tool={tool}
            workspace={workspace}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
